import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { useModulos } from "@/hooks/use-modulos";
import { useParceirosByVistoriadora } from "@/hooks/use-data";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/utils";
import { VEICULO_TIPO_LABELS } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FlaskConical,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Ban,
} from "lucide-react";

const TIPOS_VEICULO = ["auto_leve", "moto", "caminhao", "onibus"];

const STATUS_FATURA: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pendente: { label: "Pendente", variant: "secondary" },
  atrasada: { label: "Atrasada", variant: "destructive" },
  bloqueada: { label: "Bloqueada", variant: "destructive" },
  pago: { label: "Paga", variant: "default" },
  cancelado: { label: "Cancelada", variant: "outline" },
};

const STATUS_COMPRA: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pendente: { label: "Pendente", variant: "secondary" },
  pago: { label: "Paga", variant: "default" },
  cancelado: { label: "Cancelada", variant: "outline" },
};

export default function PainelTestes() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { modulos } = useModulos();

  const empresa = profile?.empresa;
  const vistoriadoraId = empresa?.id;
  const modoTeste = !!empresa?.modoTeste;

  const { data: parceiros = [] } = useParceirosByVistoriadora(vistoriadoraId);
  const [parceiroId, setParceiroId] = useState("");

  const [quantidades, setQuantidades] = useState<Record<string, number>>({});
  const totalCreditos = Object.values(quantidades).reduce((a, b) => a + b, 0);

  // ── Pós-pago: faturas_parceiro em aberto do parceiro selecionado ──────────
  const { data: faturas = [] } = useQuery<any[]>({
    queryKey: ["faturas-parceiro", "painel-testes", parceiroId],
    queryFn: () =>
      apiRequest("GET", `/api/faturas-parceiro?parceiro_id=${parceiroId}`).then((r) => r.json()),
    enabled: !modulos.creditosPrePagos && !!parceiroId,
  });
  const faturasEmAberto = faturas.filter((f) => ["pendente", "atrasada", "bloqueada"].includes(f.status));

  // ── Pré-pago: compras simuladas do parceiro selecionado ────────────────────
  const { data: compras = [] } = useQuery<any[]>({
    queryKey: ["painel-testes-creditos", parceiroId],
    queryFn: () =>
      apiRequest("GET", `/api/painel-testes/creditos?parceiroId=${parceiroId}`).then((r) => r.json()),
    enabled: modulos.creditosPrePagos && !!parceiroId,
  });

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ["faturas-parceiro"] });
    queryClient.invalidateQueries({ queryKey: ["painel-testes-creditos", parceiroId] });
  };

  const simularCompra = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/painel-testes/creditos/simular", { parceiroId, quantidades }).then((r) => r.json()),
    onSuccess: () => {
      toast({ title: "Solicitação de compra simulada criada" });
      setQuantidades({});
      invalidar();
    },
    onError: (err: any) => toast({ title: "Erro", description: err.message, variant: "destructive" }),
  });

  const pagarCompra = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/painel-testes/creditos/${id}/pagar`),
    onSuccess: () => {
      toast({ title: "Compra simulada como paga" });
      invalidar();
    },
    onError: (err: any) => toast({ title: "Erro ao pagar", description: err.message, variant: "destructive" }),
  });

  const cancelarCompra = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/painel-testes/creditos/${id}/cancelar`),
    onSuccess: () => {
      toast({ title: "Compra simulada cancelada" });
      invalidar();
    },
    onError: (err: any) => toast({ title: "Erro ao cancelar", description: err.message, variant: "destructive" }),
  });

  const pagarFatura = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/faturas-parceiro/${id}/pagar`),
    onSuccess: () => {
      toast({ title: "Fatura marcada como paga" });
      invalidar();
    },
    onError: (err: any) => toast({ title: "Erro ao pagar", description: err.message, variant: "destructive" }),
  });

  const cancelarFatura = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/faturas-parceiro/${id}/cancelar`),
    onSuccess: () => {
      toast({ title: "Fatura cancelada" });
      invalidar();
    },
    onError: (err: any) => toast({ title: "Erro ao cancelar", description: err.message, variant: "destructive" }),
  });

  const simularVencimento = useMutation({
    mutationFn: ({ id, diasAtraso }: { id: string; diasAtraso: number }) =>
      apiRequest("PATCH", `/api/faturas-parceiro/${id}/simular-vencimento`, { diasAtraso }),
    onSuccess: () => {
      toast({ title: "Vencimento simulado" });
      invalidar();
    },
    onError: (err: any) => toast({ title: "Erro ao simular vencimento", description: err.message, variant: "destructive" }),
  });

  if (!modoTeste) {
    return (
      <Shell>
        <Alert>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>
            O Painel de Testes só fica disponível com o "Modo Teste" ativado para sua
            Vistoriadora (tela Empresas).
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-6 max-w-3xl">
        <Alert>
          <FlaskConical className="w-4 h-4" />
          <AlertDescription>
            Simula compra/pagamento de um Parceiro sem depender do Asaas Sandbox. Só afeta o
            parceiro selecionado, nunca sai do sistema.
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Selecionar Parceiro</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={parceiroId} onValueChange={setParceiroId}>
              <SelectTrigger>
                <SelectValue placeholder="Escolha um parceiro de negócio" />
              </SelectTrigger>
              <SelectContent>
                {parceiros.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.nomeFantasia || p.razaoSocial}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {parceiroId && modulos.creditosPrePagos && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Simular Compra de Crédito</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {TIPOS_VEICULO.map((tipo) => (
                  <div key={tipo} className="flex items-center gap-3">
                    <Label className="w-32">{VEICULO_TIPO_LABELS[tipo] ?? tipo}</Label>
                    <Input
                      type="number"
                      min={0}
                      value={quantidades[tipo] ?? ""}
                      onChange={(e) =>
                        setQuantidades((prev) => ({
                          ...prev,
                          [tipo]: Math.max(0, parseInt(e.target.value, 10) || 0),
                        }))
                      }
                      className="w-24"
                    />
                  </div>
                ))}
                <Button
                  onClick={() => simularCompra.mutate()}
                  disabled={totalCreditos === 0 || simularCompra.isPending}
                >
                  Criar Solicitação de Compra Simulada
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Compras Simuladas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {compras.length === 0 && (
                  <p className="text-sm text-muted-foreground">Nenhuma compra simulada ainda.</p>
                )}
                {compras.map((c: any) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between border rounded-md p-3"
                  >
                    <div>
                      <p className="font-medium">{formatCurrency(Number(c.valor_total))}</p>
                      <Badge variant={STATUS_COMPRA[c.status]?.variant ?? "outline"}>
                        {STATUS_COMPRA[c.status]?.label ?? c.status}
                      </Badge>
                    </div>
                    {c.status === "pendente" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => pagarCompra.mutate(c.id)} className="gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Pagar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelarCompra.mutate(c.id)}
                          className="gap-1"
                        >
                          <XCircle className="w-3 h-3" /> Cancelar
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </>
        )}

        {parceiroId && !modulos.creditosPrePagos && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Faturas em Aberto (Pós-pago)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {faturasEmAberto.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nenhuma fatura em aberto para este parceiro no momento.
                </p>
              )}
              {faturasEmAberto.map((f: any) => (
                <FaturaRow
                  key={f.id}
                  fatura={f}
                  onPagar={() => pagarFatura.mutate(f.id)}
                  onCancelar={() => cancelarFatura.mutate(f.id)}
                  onSimularVencimento={(diasAtraso) =>
                    simularVencimento.mutate({ id: f.id, diasAtraso })
                  }
                />
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </Shell>
  );
}

function FaturaRow({
  fatura,
  onPagar,
  onCancelar,
  onSimularVencimento,
}: {
  fatura: any;
  onPagar: () => void;
  onCancelar: () => void;
  onSimularVencimento: (diasAtraso: number) => void;
}) {
  const [dias, setDias] = useState(1);
  return (
    <div className="border rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium">{formatCurrency(Number(fatura.valorTotal))}</p>
          <p className="text-xs text-muted-foreground">
            Venc.: {new Date(fatura.vencimento).toLocaleDateString("pt-BR")}
          </p>
        </div>
        <Badge variant={STATUS_FATURA[fatura.status]?.variant ?? "outline"}>
          {STATUS_FATURA[fatura.status]?.label ?? fatura.status}
        </Badge>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={onPagar} className="gap-1">
          <CheckCircle2 className="w-3 h-3" /> Pagar
        </Button>
        <Button size="sm" variant="outline" onClick={onCancelar} className="gap-1">
          <Ban className="w-3 h-3" /> Cancelar
        </Button>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            min={1}
            max={365}
            value={dias}
            onChange={(e) => setDias(Math.max(1, parseInt(e.target.value, 10) || 1))}
            className="w-16 h-8"
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onSimularVencimento(dias)}
            className="gap-1"
          >
            <Clock className="w-3 h-3" /> Simular Vencimento ({dias}d)
          </Button>
        </div>
      </div>
    </div>
  );
}
